import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HiOutlineMenu,
  HiOutlineDocumentText,
  HiOutlinePhotograph,
  HiOutlineChevronRight,
  HiOutlineChevronLeft,
  HiOutlineBookOpen,
  HiOutlineAcademicCap,
  HiOutlineClipboardCheck,
  HiOutlineCalendar,
  HiOutlineCurrencyRupee,
  HiOutlineOfficeBuilding
} from 'react-icons/hi';
import { useSearch } from "../../Context/SearchContext";
import renderArticleSkeleton from './RenderArticleSkeleton';

const Admission = () => {
  const { id: admissionId } = useParams();
  const [menuItems, setMenuItems] = useState([]);
  const [menuName, setMenuName] = useState('');
  const [activeMenuId, setActiveMenuId] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loadingMenu, setLoadingMenu] = useState(true);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [collegeName, setCollegeName] = useState('');
  const token = Cookies.get('userToken');
  const { selectedEvent } = useSearch();

  // Fetch menus for the course
  useEffect(() => {
    const fetchMenus = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/apis/user/getSingleAdmissionMenuThoughByAdmissionTypeId/${admissionId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const menus = res.data.data || [];
        setMenuItems(menus);
        
        // Extract college name from first menu item
        if (menus.length > 0 && menus[0].admissionTypeId?.adminId?.collegeName) {
          setCollegeName(menus[0].admissionTypeId.adminId.collegeName);
        }
        
        setLoadingMenu(false);
      } catch (error) {
        console.error('Error fetching menu items:', error);
        setLoadingMenu(false);
      }
    };

    if (admissionId) fetchMenus();
  }, [admissionId, token]);

  // Load first menu item's content by default
  useEffect(() => {
    if (menuItems.length > 0 && !activeMenuId) {
      handleMenuClick(menuItems[0]);
    }
  }, [menuItems]);

  // Fetch content for clicked menu item
  const handleMenuClick = async (item) => {
    const id = item._id;
    setMenuName(item.admissionMenuName);
    setActiveMenuId(id);
    setLoadingArticles(true);
    setMobileMenuOpen(false); // Close mobile menu after selection
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/apis/user/getSingleAdmissionMenuDescriptionThoughByAdmissionMenuId/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = response.data?.data || [];
      setArticles(data);
      setLoadingArticles(false);
    } catch (error) {
      console.error('Error fetching content:', error);
      setArticles([]);
      setLoadingArticles(false);
    }
  };

  useEffect(() => {
    if (menuItems.length > 0) {
      let selected = null;

      if (selectedEvent?.admissionMenuName) {
        selected = menuItems.find(item => item.admissionMenuName === selectedEvent.admissionMenuName);
      }

      if (selected) {
        handleMenuClick(selected);
      } else if (!activeMenuId) {
        handleMenuClick(menuItems[0]);
      }
    }
  }, [menuItems, selectedEvent]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  const sidebarVariants = {
    hidden: { x: -20, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  const contentVariants = {
    hidden: { x: 20, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  // Get icon based on menu name
  const getMenuIcon = (menuName) => {
    const icons = {
      'Admission': HiOutlineClipboardCheck,
      'Eligibility': HiOutlineAcademicCap,
      'Application': HiOutlineDocumentText,
      'Fees': HiOutlineCurrencyRupee,
      'Dates': HiOutlineCalendar,
      'Courses': HiOutlineBookOpen,
    };
    const Icon = icons[menuName] || HiOutlineDocumentText;
    return <Icon className="w-5 h-5" />;
  };

  // Sidebar skeleton loader
  const renderMenuSkeleton = () => (
    <div className="space-y-3">
      {[...Array(4)].map((_, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: index * 0.1 }}
          className="h-12 bg-gray-200 rounded-xl animate-pulse"
        />
      ))}
    </div>
  );

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 p-3 sm:p-4 md:p-6"
    >
      <div className="max-w-7xl mx-auto">
        {/* Mobile Menu Toggle - Light Gray */}
        <div className="md:hidden mb-4">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="w-full bg-white rounded-xl shadow-md p-4 flex items-center justify-between border border-gray-200"
          >
            <div className="flex items-center space-x-2">
              <HiOutlineMenu className="w-5 h-5 text-gray-600" />
              <span className="font-semibold text-gray-700">Menu</span>
            </div>
            <motion.div
              animate={{ rotate: mobileMenuOpen ? 90 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <HiOutlineChevronRight className="w-5 h-5 text-gray-500" />
            </motion.div>
          </motion.button>
        </div>

        <div className="flex flex-col md:flex-row gap-4 md:gap-6">
          {/* Sidebar - Light Gray */}
          <motion.div
            variants={sidebarVariants}
            className={`
              ${mobileMenuOpen ? 'block' : 'hidden'} 
              md:block 
              w-full md:w-72 lg:w-80 
              bg-white/90 backdrop-blur-sm 
              rounded-2xl shadow-xl 
              p-4 md:p-6 
              border border-gray-200
              md:sticky md:top-4 md:self-start
            `}
          >
            {/* College Info */}
            {collegeName && (
              <div className="mb-5 pb-3 border-b border-gray-200">
                <div className="flex items-center space-x-2 mb-2">
                  <div className="w-8 h-8 bg-gradient-to-r from-gray-100 to-gray-200 rounded-lg flex items-center justify-center border border-gray-200">
                    <HiOutlineOfficeBuilding className="w-5 h-5 text-gray-600" />
                  </div>
                  <span className="text-xs text-gray-500 font-medium">College</span>
                </div>
                <p className="text-gray-700 font-semibold text-sm ml-10">
                  {collegeName}
                </p>
              </div>
            )}

            {/* Header - Light Gray */}
            <div className="flex items-center space-x-2 mb-6 pb-4 border-b border-gray-200">
              <div className="w-8 h-8 bg-gradient-to-r from-gray-100 to-gray-200 rounded-lg flex items-center justify-center border border-gray-200">
                <HiOutlineMenu className="w-5 h-5 text-gray-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-700">
                Admission Menu
              </h2>
            </div>

            {/* Menu Items - Light Gray */}
            {loadingMenu ? (
              renderMenuSkeleton()
            ) : (
              <motion.ul
                variants={containerVariants}
                className="space-y-2"
              >
                {menuItems.map((item, index) => (
                  <motion.li
                    key={item._id}
                    variants={itemVariants}
                    custom={index}
                  >
                    <motion.button
                      onClick={() => handleMenuClick(item)}
                      whileHover={{ scale: 1.02, x: 5 }}
                      whileTap={{ scale: 0.98 }}
                      className={`
                        w-full text-left px-4 py-3 
                        rounded-xl transition-all duration-300
                        flex items-center space-x-3 group
                        ${activeMenuId === item._id
                          ? 'bg-gray-700 text-white shadow-lg'
                          : 'hover:bg-gray-100 text-gray-700'}
                      `}
                    >
                      <motion.div
                        whileHover={{ rotate: 360 }}
                        transition={{ duration: 0.5 }}
                        className={`
                          p-2 rounded-lg
                          ${activeMenuId === item._id
                            ? 'bg-white/20'
                            : 'bg-gray-100 group-hover:bg-gray-200'}
                        `}
                      >
                        {getMenuIcon(item.admissionMenuName)}
                      </motion.div>
                      <span className="flex-1 font-medium">{item.admissionMenuName}</span>
                      {activeMenuId === item._id && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ duration: 0.3 }}
                        >
                          <HiOutlineChevronRight className="w-5 h-5" />
                        </motion.div>
                      )}
                    </motion.button>
                  </motion.li>
                ))}
              </motion.ul>
            )}

            {/* Quick Stats - Light Gray */}
            {!loadingMenu && menuItems.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="mt-6 pt-4 border-t border-gray-200"
              >
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 border border-gray-200">
                  <p className="text-sm text-gray-600 mb-2">Quick Info</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Total Sections</span>
                    <span className="text-lg font-bold text-gray-700">{menuItems.length}</span>
                  </div>
                  <div className="w-full h-1 bg-gray-200 rounded-full mt-2">
                    <motion.div
                      className="h-1 bg-gray-400 rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${(menuItems.length / 10) * 100}%` }}
                      transition={{ duration: 0.8, delay: 0.6 }}
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>

          {/* Content Area - Light Gray */}
          <motion.div
            variants={contentVariants}
            className="flex-1 min-w-0"
          >
            <AnimatePresence mode="wait">
              {loadingArticles ? (
                <motion.div
                  key="skeleton"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  {renderArticleSkeleton()}
                </motion.div>
              ) : articles.length > 0 ? (
                <motion.div
                  key="content"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="space-y-6"
                >
                  {articles.map((article, index) => (
                    <motion.div
                      key={article._id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-4 sm:p-6 border border-gray-200"
                    >
                      <div className="flex flex-col lg:flex-row gap-6">
                        {/* Text content */}
                        <div className="flex-1">
                          {/* College Name Badge */}
                          {collegeName && (
                            <div className="inline-flex items-center space-x-2 px-3 py-1 bg-gradient-to-r from-gray-100 to-gray-200 rounded-full border border-gray-200 mb-4">
                              <HiOutlineOfficeBuilding className="w-4 h-4 text-gray-600" />
                              <span className="text-xs font-medium text-gray-700">
                                {collegeName}
                              </span>
                            </div>
                          )}

                          <div className="flex items-center space-x-2 mb-4">
                            <div className="w-8 h-8 bg-gradient-to-r from-gray-100 to-gray-200 rounded-lg flex items-center justify-center border border-gray-200">
                              <HiOutlineDocumentText className="w-5 h-5 text-gray-600" />
                            </div>
                            <h1 className="text-xl sm:text-2xl font-bold text-gray-700">
                              {menuName}
                            </h1>
                          </div>

                          <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                            {article.title}
                          </h2>

                          <div className="prose prose-sm sm:prose-base max-w-none">
                            <p className="text-gray-600 leading-relaxed whitespace-pre-line">
                              {article.description}
                            </p>
                          </div>
                        </div>

                        {/* Image */}
                        {article.image && (
                          <motion.div
                            className="lg:w-80 xl:w-96"
                            whileHover={{ scale: 1.02 }}
                            transition={{ duration: 0.3 }}
                          >
                            <div className="relative group">
                              <div className="absolute -inset-2 bg-gradient-to-r from-gray-300 to-gray-400 rounded-2xl opacity-0 group-hover:opacity-30 blur-xl transition duration-300" />
                              <img
                                src={article.image}
                                alt={article.title}
                                className="relative w-full h-48 sm:h-56 lg:h-64 object-cover rounded-xl shadow-lg border border-gray-200"
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition duration-300" />
                              <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm rounded-lg p-2 opacity-0 group-hover:opacity-100 transition duration-300 border border-gray-200">
                                <HiOutlinePhotograph className="w-5 h-5 text-gray-600" />
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              ) : (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-8 sm:p-12 text-center border border-gray-200"
                >
                  <div className="w-20 h-20 bg-gradient-to-r from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-4 border border-gray-200">
                    <HiOutlineDocumentText className="w-10 h-10 text-gray-500" />
                  </div>
                  <p className="text-gray-600 text-lg mb-2">No Content Available</p>
                  <p className="text-gray-400 text-sm">Select a menu item to view its content</p>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export default Admission;